// The family tree's vocabulary, shared by the app and the Worker. Five kinds of link are stored, total: parent
// (a is b's parent), partner, sibling, family friend, and pet (b is a's pet, their fur baby). Everything else
// (grandparents, cousins, in-laws) is found by walking these and is never stored. Labels are plain words:
// "mother", "son", "partner", never jargon.

export type Kind = 'parent' | 'partner' | 'sibling' | 'friend' | 'pet'
// How a link reads from one person's side. A pet's people are its pet parents.
export type Relation = 'parent' | 'child' | 'partner' | 'sibling' | 'friend' | 'pet' | 'petParent'
export const RELATIONS: Relation[] = ['parent', 'child', 'partner', 'sibling', 'friend', 'pet', 'petParent']

// Pets are linked only to their people, and only ever as pets: "person is of's <relation>" holds when the pet is
// whoever the relation says it is, and nobody else is a pet.
export const fits = (of: { pet: boolean }, relation: Relation, person: { pet: boolean }) =>
  of.pet === (relation === 'petParent') && person.pet === (relation === 'pet')
// What someone can be to of: a pet's people are only its pet parents.
export const relationsFor = (of: { pet: boolean }): Relation[] =>
  of.pet ? ['petParent'] : RELATIONS.filter((r) => r !== 'petParent')

// How the family refers to someone, which picks "mother" over "father" or "parent". Optional.
export type ReferAs = 'she' | 'he' | 'they'
export type Link = { id: string; kind: Kind; a: string; b: string }

// The stored form of "person is of's <relation>". Two-way kinds keep a before b, so each pair is stored once.
export function storedLink(of: string, relation: Relation, person: string): Omit<Link, 'id'> {
  if (relation === 'parent') return { kind: 'parent', a: person, b: of }
  if (relation === 'child') return { kind: 'parent', a: of, b: person }
  if (relation === 'petParent') return { kind: 'pet', a: person, b: of }
  if (relation === 'pet') return { kind: 'pet', a: of, b: person }
  const [a, b] = of < person ? [of, person] : [person, of]
  return { kind: relation, a, b }
}

// Who is at the other end of a link from self, and what they are to self.
export function relationFrom(link: Omit<Link, 'id'>, self: string): { other: string; relation: Relation } | null {
  if (link.a === self) return { other: link.b, relation: link.kind === 'parent' ? 'child' : link.kind }
  if (link.b === self) return { other: link.a, relation: link.kind === 'pet' ? 'petParent' : link.kind }
  return null
}

// Is this a one-way kind, where a and b can't swap?
export const directed = (kind: Kind) => kind === 'parent' || kind === 'pet'

// Someone entered as a son who is really the dog, or the other way round: when a person becomes a pet (or stops
// being one), their other links follow. Their parents become their pet parents and back; any other link (a partner,
// a sibling, a child of their own) can't belong to a pet, so it blocks the change until it's unlinked.
export function turnPet<L extends Omit<Link, 'id'>>(links: L[], person: string, pet: boolean) {
  const from: Kind = pet ? 'parent' : 'pet'
  const moved: L[] = []
  const blocked: L[] = []
  for (const link of links) {
    if (link.a !== person && link.b !== person) continue
    if (link.kind === from && link.b === person) moved.push({ ...link, kind: pet ? 'pet' : 'parent' })
    else blocked.push(link)
  }
  return { moved, blocked }
}

// Two generations away: found by walking two parent links, never stored. Added by way of the person in between.
export type Generation = 'grandparent' | 'grandchild'

const WORDS: Record<Relation | Generation, Record<ReferAs, string>> = {
  parent: { she: 'mother', he: 'father', they: 'parent' },
  child: { she: 'daughter', he: 'son', they: 'child' },
  partner: { she: 'partner', he: 'partner', they: 'partner' },
  sibling: { she: 'sister', he: 'brother', they: 'sibling' },
  friend: { she: 'family friend', he: 'family friend', they: 'family friend' },
  pet: { she: 'pet', he: 'pet', they: 'pet' },
  petParent: { she: 'pet mom', he: 'pet dad', they: 'pet parent' },
  grandparent: { she: 'grandmother', he: 'grandfather', they: 'grandparent' },
  grandchild: { she: 'granddaughter', he: 'grandson', they: 'grandchild' },
}

// What to call someone who is this relation to another: label('parent', 'she') is "mother".
export const label = (relation: Relation | Generation, referAs: ReferAs | null | undefined) =>
  WORDS[relation][referAs ?? 'they']

// The words for each relation as a choice, before the person is known ("Parent", "Family friend").
export const RELATION_NAMES: Record<Relation | Generation, string> = {
  parent: 'Parent',
  child: 'Child',
  partner: 'Partner',
  sibling: 'Sibling',
  friend: 'Family friend',
  pet: 'Pet (fur baby)',
  petParent: 'Pet parent',
  grandparent: 'Grandparent',
  grandchild: 'Grandchild',
}

export type Neighbor = { id: string; relation: Relation; link: string | null }
export type Orbit = Record<
  'parents' | 'petParents' | 'partners' | 'siblings' | 'children' | 'pets' | 'friends',
  Neighbor[]
>

// One person's neighborhood: everyone linked to them directly, grouped the way the orbit view lays them out.
// Brothers and sisters also count when they share a parent, even if nobody linked them as siblings; those carry no
// link of their own (link: null). Nobody appears twice: a closer kind of link wins.
export function orbit(focus: string, links: Link[]): Orbit {
  const out: Orbit = { parents: [], petParents: [], partners: [], siblings: [], children: [], pets: [], friends: [] }
  const seen = new Set<string>([focus])
  const group: Record<Relation, Neighbor[]> = {
    parent: out.parents,
    child: out.children,
    partner: out.partners,
    sibling: out.siblings,
    friend: out.friends,
    pet: out.pets,
    petParent: out.petParents,
  }
  const order: Relation[] = ['parent', 'petParent', 'partner', 'child', 'pet', 'sibling', 'friend']
  const direct = links.flatMap((link) => {
    const r = relationFrom(link, focus)
    return r ? [{ id: r.other, relation: r.relation, link: link.id }] : []
  })
  for (const relation of order) {
    for (const n of direct) {
      if (n.relation !== relation || seen.has(n.id)) continue
      seen.add(n.id)
      group[relation].push(n)
    }
    if (relation === 'sibling') {
      const parents = new Set(out.parents.map((p) => p.id))
      for (const link of links) {
        if (link.kind === 'parent' && parents.has(link.a) && !seen.has(link.b)) {
          seen.add(link.b)
          out.siblings.push({ id: link.b, relation: 'sibling', link: null })
        }
      }
    }
  }
  return out
}

// What someone can be added as from of's card: the stored relations, plus grandparents and grandchildren, who are
// added by way of the parent or child in between.
export function choicesFor(of: { pet: boolean }): (Relation | Generation)[] {
  return relationsFor(of).flatMap((r): (Relation | Generation)[] =>
    r === 'parent' ? ['parent', 'grandparent'] : r === 'child' ? ['child', 'grandchild'] : [r],
  )
}

export type Distant = { id: string; relation: Generation; via: string }

// The generation beyond the orbit: parents' parents and children's children, each with the parent or child they come
// through, in the order those sit in the orbit. Pets have no grandparents or grandchildren, and nobody already in the
// orbit shows again.
export function generations(focus: string, links: Link[], around = orbit(focus, links)) {
  const seen = new Set([focus, ...Object.values(around).flatMap((g) => g.map((n) => n.id))])
  const parentsOf = (id: string) => links.filter((l) => l.kind === 'parent' && l.b === id).map((l) => l.a)
  const childrenOf = (id: string) => links.filter((l) => l.kind === 'parent' && l.a === id).map((l) => l.b)
  const walk = (vias: Neighbor[], next: (id: string) => string[], relation: Generation) => {
    const out: Distant[] = []
    for (const via of vias) {
      for (const id of next(via.id)) {
        if (seen.has(id)) continue
        seen.add(id)
        out.push({ id, relation, via: via.id })
      }
    }
    return out
  }
  return {
    grandparents: walk(around.parents, parentsOf, 'grandparent'),
    grandchildren: walk(around.children, childrenOf, 'grandchild'),
  }
}

// Dates are kept as the family knows them: a year, a month or a day ("1921", "1921-03", "1921-03-04").
export const PARTIAL_DATE = /^\d{4}(-(0[1-9]|1[0-2])(-(0[1-9]|[12]\d|3[01]))?)?$/

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function showDate(date: string) {
  const [y, m, d] = date.split('-')
  if (!m) return y!
  const month = MONTHS[Number(m) - 1]
  return d ? `${Number(d)} ${month} ${y}` : `${month} ${y}`
}

// "1921 – 2009", "born 1950", "died 1990", or nothing when no dates are known.
export function lifespan(p: { living: boolean; born: string | null; died: string | null }) {
  if (p.born && p.died) return `${showDate(p.born)} – ${showDate(p.died)}`
  if (p.born) return `born ${showDate(p.born)}`
  if (p.died) return `died ${showDate(p.died)}`
  return ''
}
